import { Component } from '@theme/component';

/**
 * PDP "Perfectly paired with.." teaser. Lazily fetches Search & Discovery
 * complementary products through the recommendations endpoint and swaps the
 * server-rendered markup in. The dialog inside that markup is a
 * <shop-the-look-banner> component (assets/shop-the-look-banners.js) rendered
 * by snippets/shop-the-look-dialog.liquid, so all dialog behavior — open and
 * close, product-group sibling swaps, the size variant picker and
 * add-to-cart — lives there, shared with the shop-the-look banner sections.
 */
class ShopTheLook extends Component {
  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadProducts();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /** @type {MutationObserver} */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;
      this.#loadProducts();
      break;
    }
  });

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {boolean} */
  #loaded = false;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
  }

  async #loadProducts() {
    if (this.#loaded) return;
    if (this.dataset.recommendationsPerformed === 'true') return;

    const { productId, sectionId } = this.dataset;
    if (!productId || !sectionId) return;

    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=complementary`;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        this.#handleEmpty();
        return;
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const el = doc.querySelector(`shop-the-look[id="${this.id}"]`);

      if (el?.innerHTML?.trim()) {
        this.#loaded = true;
        this.dataset.recommendationsPerformed = 'true';
        this.innerHTML = el.innerHTML;
      } else {
        this.#handleEmpty();
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        this.#handleEmpty();
      }
    } finally {
      this.#activeFetch = null;
    }
  }

  #handleEmpty() {
    this.classList.add('hidden');
  }
}

if (!customElements.get('shop-the-look')) {
  customElements.define('shop-the-look', ShopTheLook);
}
